import {
  FieldSwitch,
  HelpIcon,
  HelpIconProps,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { ComponentProps, useCallback } from 'react';

import styles from './toggleBoxHead.module.scss';

export interface ToggleBoxHeadProps extends PropsWithClassName {
  isOpen: boolean;
  title: string;
  textProps?: Partial<ComponentProps<typeof Text.Paragraph>>;
  onChange?: (isOpen: boolean) => void;
  helpIcon?: HelpIconProps;
  input: Omit<FieldSwitch['props']['input'], 'onChange' | 'ref'>;
  disabled?: boolean;
}

export const useOnToggleClick = (
  onChange: ToggleBoxHeadProps['onChange'],
  isOpen: ToggleBoxHeadProps['isOpen'],
): (() => void) =>
  useCallback(() => {
    onChange?.(!isOpen);
  }, [onChange, isOpen]);

export function ToggleBoxHead({
  isOpen,
  title,
  textProps,
  className,
  helpIcon,
  onChange,
  input,
  disabled,
}: ToggleBoxHeadProps): JSX.Element {
  const onToggleClick = useOnToggleClick(onChange, isOpen);
  const rootClass = classnames(className, styles.root);

  return (
    <div className={rootClass}>
      <div className={styles.header}>
        <label htmlFor={input.id}>
          <Text.Paragraph variant="1" {...textProps} as="span">
            {title}
          </Text.Paragraph>
        </label>
        {helpIcon && <HelpIcon className={styles.helpIcon} {...helpIcon} />}
      </div>
      <FieldSwitch
        input={{
          ...input,
          onChange: onToggleClick,
          checked: isOpen,
        }}
        disabled={disabled}
      />
    </div>
  );
}
